import { useLocation, Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Home, ShoppingBag, Gift, Cake } from 'lucide-react';

export default function PageNotFound() {
    const location = useLocation();
    const pageName = location.pathname.substring(1);

    const { data: authData, isFetched } = useQuery({
        queryKey: ['user'],
        queryFn: async () => {
            try {
                const user = await base44.auth.me();
                return { user, isAuthenticated: true };
            } catch (error) {
                return { user: null, isAuthenticated: false };
            }
        }
    });

    const links = [
        { to: '/', label: 'Home', icon: Home },
        { to: '/Order', label: 'Order', icon: ShoppingBag },
        { to: '/Loyalty', label: 'Loyalty', icon: Gift },
    ];

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-white">
            <div className="max-w-md w-full text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-brand-pink-light flex items-center justify-center mb-6">
                    <Cake className="w-10 h-10 text-brand-pink-accent" />
                </div>

                <p className="text-6xl font-bold text-brand-navy tracking-tight">404</p>
                <div className="h-1 w-16 bg-brand-pink mx-auto rounded-full mt-3 mb-6" />

                <h1 className="text-2xl font-bold text-brand-navy mb-3">
                    This page has been eaten
                </h1>
                <p className="text-brand-gray leading-relaxed">
                    We couldn&apos;t find {pageName ? <span className="font-semibold text-brand-ink">&ldquo;{pageName}&rdquo;</span> : 'that page'} on
                    the Peony Gifts &amp; Kitchen app. It may have moved &mdash; but there&apos;s plenty
                    more to enjoy.
                </p>

                <div className="grid grid-cols-3 gap-3 mt-8">
                    {links.map(({ to, label, icon: Icon }) => (
                        <Link
                            key={to}
                            to={to}
                            className="flex flex-col items-center gap-2 py-4 rounded-2xl border border-black/10 hover:bg-brand-pink-light/40 transition-colors"
                        >
                            <Icon className="w-6 h-6 text-brand-navy" />
                            <span className="text-xs font-semibold text-brand-navy">{label}</span>
                        </Link>
                    ))}
                </div>

                <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 w-full mt-6 bg-brand-navy text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-opacity"
                >
                    <Home className="w-4 h-4" />
                    Back to Home
                </Link>

                {isFetched && authData?.isAuthenticated && authData.user?.role === 'admin' && (
                    <div className="mt-8 p-4 rounded-xl bg-brand-pink-light/40 border border-brand-pink text-left">
                        <p className="text-sm font-semibold text-brand-navy mb-1">Admin note</p>
                        <p className="text-sm text-brand-gray leading-relaxed">
                            This page may not have been built yet. Ask for it in the chat to add it.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}